import { t } from '@lingui/macro';

/**
 * All available permission strings.
 *
 * The file `Permissions.ts` is auto-generated from a Django script.
 */
import { User_PERMISSION } from './Permissions';
export { User_PERMISSION };

/**
 * Enums from the backend, along with mappings to frontend lang strings
 * where appropriate.
 *
 * The translations are designed to work with the LinguiJS workflow.
 * The syntax is:
 *
 * ```js
 * t('<Namespace>.<Backend value>')`<English translation>`;
 * ```
 *
 * These must be provided as **string literals**, not as variables.
 *
 * This has virtually no effect at runtime. Its only purpose is to be detected
 * by our CLI `npm run translate` task, which uses `lingui extract` to scan
 * all of our source code files looking for strings that have been tagged
 * as language strings. `t` is a special function that `lingui extract` looks
 * for, as marking a language string.
 *
 * In its normal usage as a "tagged template literal" (eg: "t`Hello!`"), `t`
 * tells `lingui extract` to generate a lang string with ID and default
 * translation set to the contents of the template literal ("Hello!").
 *
 * When used with a param (eg: "t('greeting')`Hello!`"), `t` tells `lingui extract`
 * to generate a lang string with the param as its ID ("greeting"), and the
 * template literal as the default translation.
 *
 * So what the translations in this file do, is generate lang strings with these
 * IDs and translations, which will be added to our runtime language catalog
 * after you run `npm translate`. To use these translations at runtime, you then
 * can pass the matching ID to `<Trans>` or `i18n._()`, by combining the
 * namespace string and an enum value from the backend.
 *
 * For convenience, the component `<TransEnum>` and the function `transEnum()`
 * are provided, which will do this concatenation for you, with type-checking.
 *
 * NOTE: See `TRANSLATED_ENUMS` at the bottom of this file, for instructions
 * on adding or modifying enum translations.
 */

export enum AlarmCategory_STATUS {
  active = 'active',
  retired = 'retired',
}
t('AlarmCategory_STATUS.active')`Active`;
t('AlarmCategory_STATUS.retired')`Retired`;

export enum AlarmParameter_LEVEL {
  data_check = 'data check',
  design_check = 'design check',
  alert = 'alert',
}
t('AlarmParameter_LEVEL.data check')`Data check`;
t('AlarmParameter_LEVEL.design check')`Design check`;
t('AlarmParameter_LEVEL.alert')`Alert`;

export enum AlarmParameter_TYPE {
  minimum = 'minimum',
  maximum = 'maximum',
}
t('AlarmParameter_TYPE.minimum')`Minimum`;
t('AlarmParameter_TYPE.maximum')`Maximum`;

export enum AlarmParameter_COMPARISON_TYPE {
  absolute = 'absolute',
  relative = 'relative',
  velocity = 'velocity',
}
t('AlarmParameter_COMPARISON_TYPE.absolute')`Absolute value`;
t('AlarmParameter_COMPARISON_TYPE.relative')`Relative value`;
t('AlarmParameter_COMPARISON_TYPE.velocity')`Velocity`;

export enum AlarmParameter_DIRECTION_OF_CHANGE {
  increasing = 'increasing',
  decreasing = 'decreasing',
}
t('AlarmParameter_DIRECTION_OF_CHANGE.increasing')`Increasing (+)`;
t('AlarmParameter_DIRECTION_OF_CHANGE.decreasing')`Decreasing (-)`;

export enum AlarmParameter_STATUS {
  enabled = 'enabled',
  disabled = 'disabled',
}
t('AlarmParameter_STATUS.enabled')`Enabled`;
t('AlarmParameter_STATUS.disabled')`Disabled`;

export enum AlarmReport_STATUS {
  unacknowledged = 'unacknowledged',
  acknowledged = 'acknowledged',
  data_check = 'data check',
  cancelled = 'cancelled',
}
t('AlarmReport_STATUS.unacknowledged')`Unacknowledged`;
t('AlarmReport_STATUS.acknowledged')`Acknowledged`;
t('AlarmReport_STATUS.data check')`Data check`;
t('AlarmReport_STATUS.cancelled')`Cancelled`;

export enum ChecksheetInstance_STATUS {
  in_progress = 'in progress',
  ready_for_review = 'ready for review',
  under_followup = 'under followup',
  reviewed = 'reviewed',
}
t('ChecksheetInstance_STATUS.in progress')`In progress`;
t('ChecksheetInstance_STATUS.ready for review')`Ready for review`;
t('ChecksheetInstance_STATUS.under followup')`Under follow-up`;
t('ChecksheetInstance_STATUS.reviewed')`Reviewed`;

export enum ChecksheetTemplate_FREQUENCY {
  daily = 'daily',
  twice_weekly = 'twice weekly',
  weekly = 'weekly',
  fortnightly = 'fortnightly',
  twice_monthly = 'twice monthly',
  monthly = 'monthly',
  bimonthly = 'bimonthly',
  quarterly = 'quarterly',
  annual = 'annual',
  ad_hoc = 'ad hoc',
  adaptive = 'adaptive',
}
t('ChecksheetTemplate_FREQUENCY.daily')`Daily`;
t('ChecksheetTemplate_FREQUENCY.twice weekly')`Twice weekly`;
t('ChecksheetTemplate_FREQUENCY.weekly')`Weekly`;
t('ChecksheetTemplate_FREQUENCY.fortnightly')`Fortnightly`;
t('ChecksheetTemplate_FREQUENCY.twice monthly')`Twice monthly`;
t('ChecksheetTemplate_FREQUENCY.monthly')`Monthly`;
t('ChecksheetTemplate_FREQUENCY.bimonthly')`Two monthly`;
t('ChecksheetTemplate_FREQUENCY.quarterly')`Quarterly`;
t('ChecksheetTemplate_FREQUENCY.annual')`Annual`;
t('ChecksheetTemplate_FREQUENCY.ad hoc')`Ad-hoc`;
t('ChecksheetTemplate_FREQUENCY.adaptive')`Adaptive`;

export enum ChecksheetTemplate_STATUS {
  active = 'active',
  retired = 'retired',
}
t('ChecksheetTemplate_STATUS.active')`Active`;
t('ChecksheetTemplate_STATUS.retired')`Retired`;

export enum CivilFeature_STATUS {
  active = 'active',
  retired = 'retired',
}
t('CivilFeature_STATUS.active')`Active`;
t('CivilFeature_STATUS.retired')`Retired`;

export enum Comment_TYPE {
  inspector = 'inspector',
  analysis = 'analysis',
}
t('Comment_TYPE.inspector')`Inspector`;
t('Comment_TYPE.analysis')`Analysis`;

export enum Comment_RESOURCE_TYPE {
  alarmParameter = 'AlarmParameterComment',
  alarmReport = 'AlarmReportComment',
  area = 'AreaComment',
  batch = 'BatchComment',
  client = 'ClientComment',
  obsPoint = 'ObservationPointComment',
  media = 'MediaComment',
  reading = 'ReadingComment',
  readingGap = 'ReadingGapComment',
  readingInspector = 'ReadingInspectorComment',
  routeMarch = 'RouteMarchComment',
  site = 'SiteComment',
}
t('Comment_RESOURCE_TYPE.AlarmParameterComment')`Alarm parameter`;
t('Comment_RESOURCE_TYPE.AlarmReportComment')`Alarm report`;
t('Comment_RESOURCE_TYPE.AreaComment')`Area`;
t('Comment_RESOURCE_TYPE.BatchComment')`Batch`;
t('Comment_RESOURCE_TYPE.ClientComment')`Client`;
t('Comment_RESOURCE_TYPE.ObservationPointComment')`Observation point`;
t('Comment_RESOURCE_TYPE.MediaComment')`Media`;
t('Comment_RESOURCE_TYPE.ReadingComment')`Adjusted reading`;
t('Comment_RESOURCE_TYPE.ReadingGapComment')`Reading gap`;
t('Comment_RESOURCE_TYPE.ReadingInspectorComment')`Inspector comment`;
t('Comment_RESOURCE_TYPE.RouteMarchComment')`Route march`;
t('Comment_RESOURCE_TYPE.SiteComment')`Site`;

export enum DataLogger_DAYLIGHT_SAVING_HANDLING {
  permanent_st = 'permanent_st',
  permanent_dst = 'permanent_dst',
  handles_dst = 'handles_dst',
}
t('DataLogger_DAYLIGHT_SAVING_HANDLING.permanent_st')`Permanent standard time`;
t(
  'DataLogger_DAYLIGHT_SAVING_HANDLING.permanent_dst'
)`Permanent daylight saving time`;
t('DataLogger_DAYLIGHT_SAVING_HANDLING.handles_dst')`Handles DST transitions`;

export enum DataLoggerChannel_STATUS {
  active = 'active',
  temporarily_disabled = 'temporarily disabled',
  retired = 'retired',
}
t('DataLoggerChannel_STATUS.active')`Active`;
t('DataLoggerChannel_STATUS.temporarily disabled')`Temporarily disabled`;
t('DataLoggerChannel_STATUS.retired')`Retired`;

export enum DataLogger_TIME_DEPENDENT_FIELD_NAME {
  daylight_saving_handling = 'daylight_saving_handling',
}
t(
  'DataLogger_TIME_DEPENDENT_FIELD_NAME.daylight_saving_handling'
)`Daylight Saving Handling`;

/**
 * Formula inputs come in several types
 * 1. Raw reading: The formula input consumes a raw reading from the observation
 * point that the formula is being applied to.
 * 2. Compensated reading: The formula input consumes an adjusted reading from
 * another observation point in the same area.
 * 3. Summation: Generates a "virtual reading" that aggregates adjusted values
 * from readings belonging to other observation points
 * 4. Chain: Either acts as a raw reading type, or creates "virtual readings"
 * in response to readings belonging to another observation point
 */
export enum FormulaInput_TYPE {
  comp_reading = 'comp_reading',
  raw_reading = 'raw_reading',
  summation = 'summation',
  chainable_reading = 'chainable_reading',
}
t('FormulaInput_TYPE.comp_reading')`Compensation reading`;
t('FormulaInput_TYPE.raw_reading')`Raw reading`;
t('FormulaInput_TYPE.summation')`Summation`;
t('FormulaInput_TYPE.chainable_reading')`Chainable reading`;

/**
 * Special formula constants
 */
export const FormulaConstant_DECIMAL_PLACES = 'decimal_places';
export const FormulaConstant_COMPARISON_OPERATOR = 'comparison_operator';

export enum Media_CONTENT_TYPE {
  ENGINEERING_INSPECTION = 'engineering_inspection',
  REPORT_EXPORT = 'report_export',
  ROUTE_MARCH_PHOTO = 'route_march_photo',
  SAFETY_REPORT = 'safety_report',
}
t('Media_CONTENT_TYPE.engineering_inspection')`Engineering inspection`;
t('Media_CONTENT_TYPE.report_export')`Report export`;
t('Media_CONTENT_TYPE.route_march_photo')`Route march photo`;
t('Media_CONTENT_TYPE.safety_report')`Safety report`;

export enum Media_FILE_TYPE {
  IMAGE = 'image',
  DOCUMENT = 'document',
  AUDIO = 'audio',
  VIDEO = 'video',
}
t('Media_FILE_TYPE.image')`Image`;
t('Media_FILE_TYPE.document')`Document`;
t('Media_FILE_TYPE.audio')`Audio`;
t('Media_FILE_TYPE.video')`Video`;

/**
 * We have special behavior for the following formulas.
 */
export const Formula_code_OBSOLETE = 'OBS';
export const Formula_code_RMI = 'RMI';

export enum ObservationPoint_TIME_DEPENDENT_FIELD_NAME {
  serial_number = 'serial_number',
  instrument_reliability = 'instrument_reliability',
  install_depth = 'install_depth',
  maintenance_required = 'maintenance_required',
  probed_depth = 'probed_depth',
}
t('ObservationPoint_TIME_DEPENDENT_FIELD_NAME.serial_number')`Serial number`;
t(
  'ObservationPoint_TIME_DEPENDENT_FIELD_NAME.instrument_reliability'
)`Instrument reliability`;
t('ObservationPoint_TIME_DEPENDENT_FIELD_NAME.install_depth')`Install depth`;
t(
  'ObservationPoint_TIME_DEPENDENT_FIELD_NAME.maintenance_required'
)`Maintenance required`;
t('ObservationPoint_TIME_DEPENDENT_FIELD_NAME.probed_depth')`Probed depth`;

export enum PerformanceIndicator_RESPONSE {
  NOT_OBSERVED = 'not observed',
  OBSERVED = 'observed',
  ISSUE = 'issue',
}
t('PerformanceIndicator_RESPONSE.not observed')`Not observed`;
t('PerformanceIndicator_RESPONSE.observed')`Observed`;
t('PerformanceIndicator_RESPONSE.issue')`Dam safety issue`;

export enum PerformanceIndicator_STATUS {
  active = 'active',
  retired = 'retired',
}
t('PerformanceIndicator_STATUS.active')`Active`;
t('PerformanceIndicator_STATUS.retired')`Retired`;

export enum PlotType {
  SCATTER = 'scatter',
  SPATIAL_PLAN = 'spatial_plan',
  SPATIAL_CROSS_SECTION = 'spatial_cross_section',
  SPATIAL_WANDER = 'spatial_wander',
  SURVEY_LEVELLING = 'survey_levelling',
  TIME_SERIES = 'time_series',
  CUMULATIVE = 'cumulative',
  VERTICAL_PROFILE = 'vertical_profile',
}
t('PlotType.scatter')`Scatter`;
t('PlotType.spatial_plan')`Spatial plan`;
t('PlotType.spatial_cross_section')`Spatial cross-section`;
t('PlotType.spatial_wander')`Spatial wander`;
t('PlotType.survey_levelling')`Survey levelling`;
t('PlotType.time_series')`Time series`;
t('PlotType.cumulative')`Cumulative`;
t('PlotType.vertical_profile')`Vertical profile`;

export enum ReadingsBatch_STATUS {
  unprocessed = 'unprocessed',
  confirmation_queued = 'confirmation_queued',
  making_readings_effective = 'making_readings_effective',
  alarm_checking = 'alarm_checking',
  qa_complete = 'qa_complete',
  deleting = 'deleting',
}
t('ReadingsBatch_STATUS.unprocessed')`Unprocessed`;
t('ReadingsBatch_STATUS.confirmation_queued')`Confirmation queued`;
t('ReadingsBatch_STATUS.making_readings_effective')`Making readings effective`;
t('ReadingsBatch_STATUS.alarm_checking')`Alarm checking`;
t('ReadingsBatch_STATUS.qa_complete')`QA complete`;
t('ReadingsBatch_STATUS.deleting')`Deleting`;

export enum ReadingsBatch_RAINFALL_COMMENT {
  nil = 'nil',
  low = 'low',
  moderate = 'moderate',
  heavy = 'heavy',
}
t('ReadingsBatch_RAINFALL_COMMENT.nil')`Nil`;
t('ReadingsBatch_RAINFALL_COMMENT.low')`Low`;
t('ReadingsBatch_RAINFALL_COMMENT.moderate')`Moderate`;
t('ReadingsBatch_RAINFALL_COMMENT.heavy')`Heavy`;

export enum ReadingError {
  NOT_A_NUMBER = 105,
}

export enum ReadingsFile_STATUS {
  uploaded = 'uploaded',
  parse_error = 'parse_error',
  processed = 'processed',
  processing = 'processing',
  processing_error = 'processing_error',
}
t('ReadingsFile_STATUS.uploaded')`Uploaded`;
t('ReadingsFile_STATUS.processing')`Processing`;
t('ReadingsFile_STATUS.parse_error')`Validation error `;
t('ReadingsFile_STATUS.processed')`Validated`;
t('ReadingsFile_STATUS.processing_error')`Processing Error`;

export enum ReadingProcessingRequest_REASON {
  obs_point_formula_change = 'observation point formula change',
  obs_point_formula_constant_value_change = 'observation point formula constant value change',
  obs_point_formula_input_change = 'observation point formula input change',
  site_time_dependent_field_change = 'site time dependent field change',
  manually_requested = 'manually requested',
}
t(
  'ReadingProcessingRequest_REASON.observation point formula change'
)`Observation point formula change`;
t(
  'ReadingProcessingRequest_REASON.observation point formula constant value change'
)`Observation point formula constant value change`;
t(
  'ReadingProcessingRequest_REASON.observation point formula input change'
)`Observation point formula input change`;
t(
  'ReadingProcessingRequest_REASON.site time dependent field change'
)`Site time dependent field change`;
t('ReadingProcessingRequest_REASON.manually requested')`Manually requested`;

export enum ReadingProcessingRequest_STATUS {
  new = 'new',
  cancelled = 'cancelled',
  queued = 'queued',
  processing = 'processing',
  complete = 'complete',
  error = 'error',
}
t('ReadingProcessingRequest_STATUS.new')`New`;
t('ReadingProcessingRequest_STATUS.cancelled')`Cancelled`;
t('ReadingProcessingRequest_STATUS.queued')`Queued`;
t('ReadingProcessingRequest_STATUS.processing')`Processing`;
t('ReadingProcessingRequest_STATUS.complete')`Complete`;
t('ReadingProcessingRequest_STATUS.error')`Error`;

export enum ReadingRetirementRequest_STATUS {
  new = 'new',
  cancelled = 'cancelled',
  queued = 'queued',
  processing = 'processing',
  complete = 'complete',
  error = 'error',
}
t('ReadingRetirementRequest_STATUS.new')`New`;
t('ReadingRetirementRequest_STATUS.cancelled')`Cancelled`;
t('ReadingRetirementRequest_STATUS.queued')`Queued`;
t('ReadingRetirementRequest_STATUS.processing')`Processing`;
t('ReadingRetirementRequest_STATUS.complete')`Complete`;
t('ReadingRetirementRequest_STATUS.error')`Error`;

export enum ReportFilterInfo_EXPRESSION {
  exact = 'exact',
  icontains = 'icontains',
  in = 'in',
  isnull = 'isnull',
  gte = 'gte',
  lte = 'lte',
  withinlast = 'withinlast',
  withinnext = 'withinnext',
}

export enum ReportInfo_REPORT {
  area_report = 'area_report',
  alarm_notification = 'alarm_notification',
  alarm_parameter = 'alarm_parameter',
  alarm_report = 'alarm_report',
  alarm_comments = 'alarm_comments',
  audit_log_report = 'audit_log_report',
  checksheet_instance_report = 'checksheet_instance_report',
  checksheet_template_report = 'checksheet_template_report',
  client_report = 'client_report',
  comment_report = 'comment_report',
  data_logger_channel_report = 'data_logger_channel_report',
  file_gate_report = 'file_gate_report',
  inspection_batch = 'inspection_batch',
  instrument_type_report = 'instrument_type_report',
  instrument_reliability_report = 'instrument_reliability_report',
  maintenance_required_report = 'maintenance_required_report',
  media_report = 'media_report',
  my_exports_report = 'my_exports_report',
  observation_point_report = 'observation_point_report',
  observation_point_formula_report = 'observation_point_formula_report',
  observation_point_formula_constant_report = 'observation_point_formula_constant_report',
  plot_set = 'plot_set',
  reading_comments = 'reading_comments',
  readings_batch = 'readings_batch',
  reading_processing_request = 'reading_processing_request',
  reading_retirement_request = 'reading_retirement_request',
  route_march_schedule_report = 'route_march_schedule_report',
  saved_report_report = 'saved_report_report',
  site_report = 'site_report',
  stored_plot = 'stored_plot',
  user = 'user',
}
t('ReportInfo_REPORT.area_report')`Area`;
t('ReportInfo_REPORT.alarm_notification')`Alarm notification`;
t('ReportInfo_REPORT.alarm_parameter')`Alarm parameter`;
t('ReportInfo_REPORT.alarm_report')`Alarm report`;
t('ReportInfo_REPORT.alarm_comments')`Alarm comments`;
t('ReportInfo_REPORT.audit_log_report')`Audit log`;
t('ReportInfo_REPORT.checksheet_instance_report')`Checksheet instance`;
t('ReportInfo_REPORT.checksheet_template_report')`Checksheet template`;
t('ReportInfo_REPORT.client_report')`Client`;
t('ReportInfo_REPORT.comment_report')`Comment`;
t('ReportInfo_REPORT.data_logger_channel_report')`Logger calibration`;
t('ReportInfo_REPORT.file_gate_report')`File upload gate`;
t('ReportInfo_REPORT.inspection_batch')`Inspection reports`;
t('ReportInfo_REPORT.instrument_type_report')`Instrument type`;
t('ReportInfo_REPORT.instrument_reliability_report')`Instrument reliability`;
t('ReportInfo_REPORT.maintenance_required_report')`Maintenance required`;
t('ReportInfo_REPORT.media_report')`Media`;
t('ReportInfo_REPORT.my_exports_report')`My Exports`;
t('ReportInfo_REPORT.observation_point_report')`Observation point`;
t('ReportInfo_REPORT.observation_point_formula_report')`Formula assignment`;
t(
  'ReportInfo_REPORT.observation_point_formula_constant_report'
)`Formula constants`;
t('ReportInfo_REPORT.plot_set')`Plot set`;
t('ReportInfo_REPORT.reading_comments')`Reading comments`;
t('ReportInfo_REPORT.readings_batch')`Readings batch`;
t('ReportInfo_REPORT.reading_retirement_request')`Retire adjusted readings`;
t('ReportInfo_REPORT.reading_processing_request')`Reprocessing requests`;
t('ReportInfo_REPORT.route_march_schedule_report')`Route march`;
t('ReportInfo_REPORT.saved_report_report')`Saved report`;
t('ReportInfo_REPORT.site_report')`Site`;
t('ReportInfo_REPORT.stored_plot')`Stored plot`;
t('ReportInfo_REPORT.user')`User`;

export enum RouteMarch_STATUS {
  active = 'active',
  retired = 'retired',
}
t('RouteMarch_STATUS.active')`Active`;
t('RouteMarch_STATUS.retired')`Retired`;

export enum RouteMarch_TYPE {
  general = 'general',
  emergency = 'emergency',
  inspection = 'inspection',
}
t('RouteMarch_TYPE.general')`General`;
t('RouteMarch_TYPE.emergency')`Emergency`;
t('RouteMarch_TYPE.inspection')`Inspection`;

export enum Site_TIME_DEPENDENT_FIELD_NAME {
  asbuilt_angle = 'asbuilt_angle',
  cap_rl = 'cap_rl',
  ground_rl = 'ground_rl',
}
t('Site_TIME_DEPENDENT_FIELD_NAME.asbuilt_angle')`As built angle`;
t('Site_TIME_DEPENDENT_FIELD_NAME.cap_rl')`Cap RL`;
t('Site_TIME_DEPENDENT_FIELD_NAME.ground_rl')`Ground RL`;

export enum StoredList_TYPE {
  'latest_reading' = 'LatestReadingStoredList',
  'nearest_reading' = 'NearestReadingStoredList',
  'selected_list' = 'SelectedListStoredList',
  'summary_report' = 'SummaryReportStoredList',
}
t('StoredList_TYPE.LatestReadingStoredList')`Latest reading`;
t('StoredList_TYPE.NearestReadingStoredList')`Nearest reading`;
t('StoredList_TYPE.SelectedListStoredList')`Selected list`;
t('StoredList_TYPE.SummaryReportStoredList')`Observation point summary report`;

export enum PlotSet_DISPLAY_OPTION {
  show = 'show',
  hide = 'hide',
  per_plot = 'per_plot',
}
t('PlotSet_DISPLAY_OPTION.show')`Show`;
t('PlotSet_DISPLAY_OPTION.hide')`Hide`;
t('PlotSet_DISPLAY_OPTION.per_plot')`Per plot`;

export type PlotAxisSide = 'left' | 'right' | 'right2' | 'bottom';
export enum StoredPlotAnnotationAxis {
  X = 'X',
  Y = 'Y',
}

t('StoredPlotAnnotationAxis.X')`X axis`;
t('StoredPlotAnnotationAxis.Y')`Y axis`;

export enum StoredPlotAnnotationLabelPosition {
  LEFT = 'left',
  RIGHT = 'right',
  ABOVE = 'above',
  BELOW = 'below',
}

export enum StoredPlotAnnotationType {
  NUMERIC = 'numeric',
  DATETIME = 'datetime',
  SURVEY = 'survey',
}

export enum StoredPlotAnnotation_LINE_STYLE {
  FULL = 'full',
  STUB = 'stub',
}

export enum StoredPlotAnnotation_STUB_POSITION {
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
}

export enum StoredPlot_LAYOUT {
  'L100' = '100%',
  'L50_50' = '50%/50%',
  'L60_40' = '60%/40%',
  'L75_25' = '75%/25%',
  'L80_20' = '80%/20%',
  'L33_33_33' = '33%/33%/33%',
  'L50_25_25' = '50%/25%/25%',
  'L60_20_20' = '60%/20%/20%',
  'L25_25_25_25' = '25%/25%/25%/25%',
  'L40_20_20_20' = '40%/20%/20%/20%',
}

t('StoredPlot_LAYOUT.100%')`100%`;
t('StoredPlot_LAYOUT.50%/50%')`50% / 50%`;
t('StoredPlot_LAYOUT.60%/40%')`60% / 40%`;
t('StoredPlot_LAYOUT.75%/25%')`75% / 25%`;
t('StoredPlot_LAYOUT.80%/20%')`80% / 20%`;
t('StoredPlot_LAYOUT.33%/33%/33%')`33% / 33% / 33%`;
t('StoredPlot_LAYOUT.50%/25%/25%')`50% / 25% / 25%`;
t('StoredPlot_LAYOUT.60%/20%/20%')`60% / 20% / 20%`;
t('StoredPlot_LAYOUT.25%/25%/25%/25%')`25% / 25% / 25% / 25%`;
t('StoredPlot_LAYOUT.40%/20%/20%/20%')`40% / 20% / 20% / 20%`;

export enum StoredSurveyLevellingPlot_ERROR_BARS {
  INITIAL_AND_LATEST = 'initial_and_latest',
  PREVIOUS_AND_LATEST = 'previous_and_latest',
}

t(
  'StoredSurveyLevellingPlot_ERROR_BARS.initial_and_latest'
)`Initial and latest`;
t(
  'StoredSurveyLevellingPlot_ERROR_BARS.previous_and_latest'
)`Previous and latest`;

// Additional "long version" translations of the error bars types
t(
  'StoredSurveyLevellingPlot_ERROR_BARS__LONG.initial_and_latest'
)`Initial and latest error bars`;
t(
  'StoredSurveyLevellingPlot_ERROR_BARS__LONG.previous_and_latest'
)`Previous and latest error bars`;

export enum StoredSpatialWanderPlot_ERROR_ELLIPSE {
  INITIAL_AND_LATEST = 'initial_and_latest',
  PREVIOUS_AND_LATEST = 'previous_and_latest',
}
t(
  'StoredSpatialWanderPlot_ERROR_ELLIPSE.initial_and_latest'
)`Initial and latest`;
t(
  'StoredSpatialWanderPlot_ERROR_ELLIPSE.previous_and_latest'
)`Previous and latest`;

export enum StoredSpatialPlanPlot_DATA_TABLE_STYLE {
  STANDARD = 'standard',
  DEFORMATION_SURVEY = 'deformation_survey',
}
t('StoredSpatialPlanPlot_DATA_TABLE_STYLE.standard')`Standard`;
t(
  'StoredSpatialPlanPlot_DATA_TABLE_STYLE.deformation_survey'
)`Deformation survey`;

/**
 * The status of a back-end task. These are actually Celery task "states",
 * and this list may not be exhaustive because you can define additional custom
 * states.
 *
 * @see https://docs.celeryproject.org/en/stable/userguide/tasks.html#built-in-states
 * @see https://github.com/node-celery-ts/node-celery-ts/blob/1d86268a4844276c19296e8f80c9aa9da61088a3/src/messages.ts#L131
 */
export enum Task_STATUS {
  PENDING = 'PENDING',
  STARTED = 'STARTED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  REVOKED = 'REVOKED',
}
export const Task_STATUS_IN_PROGRESS = [
  Task_STATUS.PENDING,
  Task_STATUS.STARTED,
  Task_STATUS.RETRY,
];

export enum TimeLagMonitor_MODE {
  cross_correlation = 'cross_correlation',
  find_peaks = 'find_peaks',
}
t('TimeLagMonitor_MODE.cross_correlation')`Cross correlation`;
t('TimeLagMonitor_MODE.find_peaks')`Find peaks`;

export type IntervalUnit =
  | 'years'
  | 'months'
  | 'weeks'
  | 'days'
  | 'hours'
  | 'minutes';
export const INTERVAL_UNITS: IntervalUnit[] = [
  'minutes',
  'hours',
  'days',
  'weeks',
  'months',
  'years',
];

// Translations of the interval types, as name of that type.
// See `util/i18n-utils.tsx` and `util/backendapi/types/Enum.tsx` for information
// on how these translations work.
t('interval.type.years')`years`;
t('interval.type.months')`months`;
t('interval.type.weeks')`weeks`;
t('interval.type.days')`days`;
t('interval.type.hours')`hours`;
t('interval.type.minutes')`minutes`;

// Translations of a count of each interval type.
t('interval.count.years')`{count, plural, one {1 year} other {# years}}`;
t('interval.count.months')`{count, plural, one {1 month} other {# months}}`;
t('interval.count.weeks')`{count, plural, one {1 week} other {# weeks}}`;
t('interval.count.days')`{count, plural, one {1 day} other {# days}}`;
t('interval.count.hours')`{count, plural, one {1 hour} other {# hours}}`;
t('interval.count.minutes')`{count, plural, one {1 minute} other {# minutes}}`;

/**
 * When you translate a new enum, add its name and possible values to this list.
 *
 * Also, run `npm run translate` or `make dev-frontend-update-translations`,
 * which will update `frontend/src/assets/locales/en.js`. Commit the updated
 * version of the file into Git.
 *
 * NOTE: This mapping is not directly used at runtime, but allows us to test for
 * missing translations and to generate stricter type checks. The important
 * part is that:
 *
 * 1. Each **key** of `TRANSLATED_ENUMS` is a string, which acts as a sort of
 * namespace for the IDs of an enum's language strings. This string must exactly
 * match the part before the `.` in the corresponding translation's ID:
 *     `` t('MATCHTHISPART.whatever)`whatever` ``
 * 2. Each **value** of `TRANSLATED ENUMS` is an array of the possible values of that
 * enum. This is not checked at run-time, but it is used to generate type checks,
 * and there is a Jest test that uses it to generate every expected enum language
 * string ID and check for its presence in the translation catalog. Each of these
 * strings must exactly match the part after the `.` in the corresponding
 * translation's ID:
 *     `` t('whatever.MATCHTHISPART')`whatever` ``
 *
 * Although the keys mostly match the names of enum variables in `Model.ts`,
 * this is not enforced or enforceable without special Babel plugins.
 */
export const TRANSLATED_ENUMS = {
  AlarmParameter_LEVEL: Object.values(AlarmParameter_LEVEL),
  AlarmParameter_TYPE: Object.values(AlarmParameter_TYPE),
  AlarmParameter_COMPARISON_TYPE: Object.values(AlarmParameter_COMPARISON_TYPE),
  AlarmParameter_DIRECTION_OF_CHANGE: Object.values(
    AlarmParameter_DIRECTION_OF_CHANGE
  ),
  AlarmParameter_STATUS: Object.values(AlarmParameter_STATUS),
  AlarmReport_STATUS: Object.values(AlarmReport_STATUS),
  ChecksheetInstance_STATUS: Object.values(ChecksheetInstance_STATUS),
  ChecksheetTemplate_FREQUENCY: Object.values(ChecksheetTemplate_FREQUENCY),
  ChecksheetTemplate_STATUS: Object.values(ChecksheetTemplate_STATUS),
  Comment_TYPE: Object.values(Comment_TYPE),
  Comment_RESOURCE_TYPE: Object.values(Comment_RESOURCE_TYPE),
  DataLogger_DAYLIGHT_SAVING_HANDLING: Object.values(
    DataLogger_DAYLIGHT_SAVING_HANDLING
  ),
  DataLogger_TIME_DEPENDENT_FIELD_NAME: Object.values(
    DataLogger_TIME_DEPENDENT_FIELD_NAME
  ),
  DataLoggerChannel_STATUS: Object.values(DataLoggerChannel_STATUS),
  FormulaInput_TYPE: Object.values(FormulaInput_TYPE),
  Media_CONTENT_TYPE: Object.values(Media_CONTENT_TYPE),
  Media_FILE_TYPE: Object.values(Media_FILE_TYPE),
  ReadingsBatch_STATUS: Object.values(ReadingsBatch_STATUS),
  ReadingsBatch_RAINFALL_COMMENT: Object.values(ReadingsBatch_RAINFALL_COMMENT),
  ReadingsFile_STATUS: Object.values(ReadingsFile_STATUS),
  ReadingProcessingRequest_REASON: Object.values(
    ReadingProcessingRequest_REASON
  ),
  ReadingProcessingRequest_STATUS: Object.values(
    ReadingProcessingRequest_STATUS
  ),
  ReadingRetirementRequest_STATUS: Object.values(
    ReadingRetirementRequest_STATUS
  ),
  ReportInfo_REPORT: Object.values(ReportInfo_REPORT),
  RouteMarch_STATUS: Object.values(RouteMarch_STATUS),
  RouteMarch_TYPE: Object.values(RouteMarch_TYPE),
  ObservationPoint_TIME_DEPENDENT_FIELD_NAME: Object.values(
    ObservationPoint_TIME_DEPENDENT_FIELD_NAME
  ),
  Site_TIME_DEPENDENT_FIELD_NAME: Object.values(Site_TIME_DEPENDENT_FIELD_NAME),
  StoredList_TYPE: Object.values(StoredList_TYPE),
  StoredPlot_LAYOUT: Object.values(StoredPlot_LAYOUT),
  StoredSurveyLevellingPlot_ERROR_BARS: Object.values(
    StoredSurveyLevellingPlot_ERROR_BARS
  ),
  StoredSurveyLevellingPlot_ERROR_BARS__LONG: Object.values(
    StoredSurveyLevellingPlot_ERROR_BARS
  ),
  StoredSpatialWanderPlot_ERROR_ELLIPSE: Object.values(
    StoredSpatialWanderPlot_ERROR_ELLIPSE
  ),
  StoredSpatialPlanPlot_DATA_TABLE_STYLE: Object.values(
    StoredSpatialPlanPlot_DATA_TABLE_STYLE
  ),
  PerformanceIndicator_RESPONSE: Object.values(PerformanceIndicator_RESPONSE),
  PerformanceIndicator_STATUS: Object.values(PerformanceIndicator_STATUS),
  PlotType: Object.values(PlotType),
  StoredPlotAnnotationAxis: Object.values(StoredPlotAnnotationAxis),
  TimeLagMonitor_MODE: Object.values(TimeLagMonitor_MODE),
  'interval.type': INTERVAL_UNITS,
  'interval.count': INTERVAL_UNITS,
} as const;
